import { request } from '@umijs/max';

const QMS_API_PREFIX = '/api/qms/v1';
let qmsSessionContext: { tenantUuid?: string; organizationUuid?: string } = {};

export function setQmsSessionContext(context: { tenantUuid?: string; organizationUuid?: string }) {
  qmsSessionContext = context;
}

function qmsRequest<T>(url: string, options?: Record<string, unknown>) {
  const headers = {
    ...((options?.headers as Record<string, string> | undefined) || {}),
    ...(qmsSessionContext.tenantUuid ? { 'x-tenant-id': qmsSessionContext.tenantUuid } : {}),
    ...(qmsSessionContext.organizationUuid ? { 'x-organization-id': qmsSessionContext.organizationUuid } : {}),
  };
  return request<T>(url, { ...options, headers });
}

export interface QmsInspectionTask {
  uuid: string;
  taskCode: string;
  inspectionType: 'INCOMING' | 'PROCESS' | 'FINAL' | 'OUTBOUND';
  sourceSystem?: string;
  sourceDocumentNo: string;
  materialName: string;
  batchNo?: string;
  serialNo?: string;
  quantity: string;
  status: string;
}

export interface CreateInspectionTaskPayload {
  inspectionType: 'INCOMING' | 'PROCESS' | 'FINAL' | 'OUTBOUND';
  sourceSystem: string;
  sourceDocumentNo: string;
  sourceObjectId: string;
  sourceLineNo?: string;
  materialCode: string;
  materialName: string;
  batchNo?: string;
  serialNo?: string;
  quantity: number;
  unit: string;
  planSnapshot: {
    planCode: string;
    planVersion: string;
    sampleSize: number;
    requiredCharacteristics: string[];
  };
}

export type InspectionPlanApplicability = {
  inspectionTypes?: Array<'INCOMING' | 'PROCESS' | 'FINAL' | 'OUTBOUND'>;
  materialCodes?: string[];
  supplierNames?: string[];
  outboundTypes?: string[];
  lotPrefixes?: string[];
  priority?: number;
};

export type CreateInspectionPlanPayload = {
  planCode: string;
  planName: string;
  planVersion: string;
  applicableReference: string;
  characteristics: Record<string, unknown>;
  applicability?: InspectionPlanApplicability;
};

export function getInspectionTasks() {
  return qmsRequest<QmsInspectionTask[]>(`${QMS_API_PREFIX}/inspection-tasks`);
}

export interface QmsQualityAnalytics {
  taskCount: number;
  pendingTaskCount: number;
  conclusionCount: number;
  releasedCount: number;
  openNcrCount: number;
  openCapaCount: number;
  inspectionStatus: Record<string, number>;
  inspectionType: Record<string, number>;
  ncrSeverity: Record<string, number>;
  dispositionStatus: Record<string, number>;
  conclusionDecision: Record<string, number>;
  recordResult: Record<string, number>;
  recentTaskTrend: Array<{ date: string; created: number; completed: number }>;
}

export function getQualitySummary() {
  return qmsRequest<QmsQualityAnalytics>(`${QMS_API_PREFIX}/analytics/summary`);
}

export function getNcrs() {
  return qmsRequest<any[]>(`${QMS_API_PREFIX}/ncrs`);
}

export function getDispositions() {
  return qmsRequest<any[]>(`${QMS_API_PREFIX}/dispositions`);
}
export function getWmsDispositionCandidates() {
  return qmsRequest<Array<{
    heldBalanceUuid: string;
    heldBalanceVersion: number;
    inboundOrderCode: string;
    supplierName: string;
    materialCode: string;
    materialName: string;
    batchNo?: string;
    lpn?: string;
    locationCode: string;
    availableQuantity: number;
    frozenQuantity: number;
    unit: string;
  }>>(`${QMS_API_PREFIX}/disposition-candidates/wms`);
}

export function getCapas() {
  return qmsRequest<any[]>(`${QMS_API_PREFIX}/capas`);
}

export function getInspectionPlans() { return qmsRequest<any[]>(`${QMS_API_PREFIX}/inspection-plans`); }
export function getInspectionTask(uuid: string) { return qmsRequest<any>(`${QMS_API_PREFIX}/inspection-tasks/${uuid}`); }
export function getTraceability(key: string) { return qmsRequest<any>(`${QMS_API_PREFIX}/traceability`, { params: { key } }); }
export function getQualitySettings() { return qmsRequest<any[]>(`${QMS_API_PREFIX}/quality-settings`); }

function command<T>(url: string, data?: unknown) {
  return qmsRequest<T>(url, { method: 'POST', data, headers: { 'Idempotency-Key': crypto.randomUUID() } });
}

export function createInspectionTask(data: CreateInspectionTaskPayload) { return command<QmsInspectionTask>(`${QMS_API_PREFIX}/inspection-tasks`, data); }
export function startInspectionTask(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/start`); }
export function createInspectionRecord(uuid: string, data: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/records`, data); }
export function submitInspectionTask(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/submit`); }
export function judgeInspectionTask(uuid: string, data: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/judge`, data); }
export function approveInspectionConclusion(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/conclusion/approve`); }
export function publishInspectionConclusion(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-tasks/${uuid}/conclusion/publish`); }
export function createNcr(data: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/ncrs`, data); }
export function reviewNcr(uuid: string) { return command(`${QMS_API_PREFIX}/ncrs/${uuid}/review`); }
export function createDisposition(data: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/dispositions`, data); }
export function approveDisposition(uuid: string) { return command(`${QMS_API_PREFIX}/dispositions/${uuid}/approve`); }
export function retryDisposition(uuid: string) { return command(`${QMS_API_PREFIX}/dispositions/${uuid}/retry`); }
export function createCapa(data: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/capas`, data); }
export function approveCapa(uuid: string) { return command(`${QMS_API_PREFIX}/capas/${uuid}/approve`); }
export function verifyCapa(uuid: string) { return command(`${QMS_API_PREFIX}/capas/${uuid}/verify`); }
export function closeCapa(uuid: string) { return command(`${QMS_API_PREFIX}/capas/${uuid}/close`); }
export function createInspectionPlan(data: CreateInspectionPlanPayload) { return command(`${QMS_API_PREFIX}/inspection-plans`, data); }
export function submitInspectionPlan(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-plans/${uuid}/submit`); }
export function publishInspectionPlan(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-plans/${uuid}/publish`); }
export function retireInspectionPlan(uuid: string) { return command(`${QMS_API_PREFIX}/inspection-plans/${uuid}/retire`); }
export function saveQualitySetting(key: string, value: Record<string, unknown>) { return command(`${QMS_API_PREFIX}/quality-settings/${key}`, { value }); }
